import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useReducer, useRef, useState } from "react"
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, pointerWithin, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { defaultScheduleFilters, defaultScheduleState, ScheduleFilters, scheduleFiltersReducer, ScheduleMode, UNASSIGNED_CREW_ID, rehydrateScheduleFilters } from "./scheduleModel"
import useServiceTicketsEnabled from "../../hooks/useServiceTicketsEnabled"
import { useSavedState } from "../../hooks/useSavedState"
import ScheduleDayView from "./DayView/ScheduleDayView"
import ScheduleWeekView, { ScheduleWeekViewHandle } from "./WeekView/ScheduleWeekView"
import ScheduleMonthView, { ScheduleMonthViewHandle } from "./MonthView/ScheduleMonthView"
import ScheduleTitleBar from "./ScheduleTitleBar"
import { CalendarSearchHighlightProvider } from './CalendarSearchHighlightContext'
import ReorderDayView from "./DayView/ReorderView/ReorderDayView"
import OptimizeScheduleModal from "./OptimizeSchedule/OptimizeScheduleModal"
import { CrewLeadSelection } from "./OptimizeSchedule/optimizeScheduleModel"
import CreateEventDrawer from "./CreateEvent/CreateEventDrawer"
import CreateEventFromJobModal from "./CreateEvent/CreateEventFromJobModal"
import CreateEventFromRouteModal from "./CreateEvent/CreateEventFromRouteModal"
import JobDragOverlay from "./CreateEvent/JobDragOverlay"
import RouteDragOverlay from "./CreateEvent/RouteDragOverlay"
import dayjs, { Dayjs } from "dayjs"
import { ScheduleProvider } from "./ScheduleContext"
import { useScheduleHeaderActions } from "./ScheduleHeaderActionsContext"
import { RescheduleCandidate, SchedulingJob } from "./scheduleApi"
import { rescheduleCandidateToInitialEvent, rescheduleCandidateToSchedulingJob } from "./CreateEvent/scheduleCreateEventModel"
import { Route } from "../RouteManagement/routingApi"

export type ScheduledContentRef = {
  openCreateEventDrawer: () => void
}

const ScheduleContent = forwardRef((_: unknown, ref: Ref<ScheduledContentRef>) => {
  const [savedView, setSavedView] = useSavedState<ScheduleMode>('schedule-view-mode', ScheduleMode.Day)
  const [savedFilters, setSavedFilters] = useSavedState<ScheduleFilters>('schedule-filters', defaultScheduleFilters)
  const [scheduleState, dispatch] = useReducer(scheduleFiltersReducer, defaultScheduleState)
  const showServiceTickets = useServiceTicketsEnabled()
  const { setHeaderState } = useScheduleHeaderActions()

  useEffect(() => {
    setHeaderState({ view: scheduleState.view, selectedDate: scheduleState.selectedDate })
  }, [scheduleState.view, scheduleState.selectedDate, setHeaderState])

  const [createEventDrawerOpen, setCreateEventDrawerOpen] = useSavedState('create-event-drawer-open', window.innerWidth >= 2000)
  const [createEventInitialTab, setCreateEventInitialTab] = useState('1')
  const [optimizeModalDate, setOptimizeModalDate] = useState<Dayjs | null>(null)

  useImperativeHandle(ref, () => ({
    openCreateEventDrawer: () => setCreateEventDrawerOpen(true)
  }), [setCreateEventDrawerOpen])

  const [draggedJob, setDraggedJob] = useState<SchedulingJob | null>(null)
  const [droppedJobData, setDroppedJobData] = useState<{ job: SchedulingJob; date: Dayjs; minutes?: number } | null>(null)
  const [draggedRoute, setDraggedRoute] = useState<Route | null>(null)
  const [droppedRouteData, setDroppedRouteData] = useState<{ route: Route; date: Dayjs } | null>(null)
  const [draggedRescheduleCandidate, setDraggedRescheduleCandidate] = useState<RescheduleCandidate | null>(null)
  const [droppedRescheduleData, setDroppedRescheduleData] = useState<{ candidate: RescheduleCandidate; date: Dayjs; minutes?: number } | null>(null)

  const weekViewRef = useRef<ScheduleWeekViewHandle>(null)
  const monthViewRef = useRef<ScheduleMonthViewHandle>(null)

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  })
  const sensors = useSensors(pointerSensor)

  useEffect(() => {
    if (savedView !== scheduleState.view) {
      dispatch({ type: 'SET_VIEW', payload: savedView })
    }
  }, [savedView])

  useEffect(() => {
    dispatch({ type: 'SET_FILTERS', payload: rehydrateScheduleFilters(savedFilters, showServiceTickets) })
  }, [savedFilters, showServiceTickets])

  const onTodayClick = () => {
    dispatch({ type: 'SET_DATE', payload: dayjs() })
  }

  const onPrevClick = () => {
    const unitMap = {
      [ScheduleMode.Month]: 'month' as const,
      [ScheduleMode.Week]: 'week' as const,
      [ScheduleMode.Day]: 'day' as const,
    }
    const unit = unitMap[scheduleState.view]
    dispatch({ type: 'SET_DATE', payload: scheduleState.selectedDate.subtract(1, unit) })
  }

  const unitMap = {
    [ScheduleMode.Month]: 'month' as const,
    [ScheduleMode.Week]: 'week' as const,
    [ScheduleMode.Day]: 'day' as const,
  }

  const onNextClick = () => {
    const unit = unitMap[scheduleState.view]
    dispatch({ type: 'SET_DATE', payload: scheduleState.selectedDate.add(1, unit) })
  }

  const onDateChange = (date: Dayjs) => {
    dispatch({ type: 'SET_DATE', payload: date })
  }

  const onViewChange = (view: ScheduleMode) => {
    setSavedView(view)
    dispatch({ type: 'SET_VIEW', payload: view })
    window.posthog.capture('schedule-view-changed', { view: unitMap[view] })
  }

  const onFiltersChange = (filters: ScheduleFilters) => {
    setSavedFilters(filters)
    dispatch({ type: 'SET_FILTERS', payload: filters })
  }

  const onEnterReorderMode = (selectedCrewLeadId: number | typeof UNASSIGNED_CREW_ID) => {
    dispatch({ type: 'ENTER_REORDER_MODE', payload: selectedCrewLeadId })
  }

  const onExitReorderMode = () => {
    dispatch({ type: 'EXIT_REORDER_MODE' })
  }

  const onSaveReorder = () => {
    dispatch({ type: 'EXIT_REORDER_MODE' })
  }

  const onOpenOptimize = (date: Dayjs) => {
    setOptimizeModalDate(date)
  }

  const onCancelOptimize = () => {
    setOptimizeModalDate(null)
  }

  const onSubmitOptimize = (date: Dayjs, crewLeadId: CrewLeadSelection) => {
    setOptimizeModalDate(null)
    dispatch({ type: 'ENTER_REORDER_MODE_FOR_DATE', payload: { date, crewLeadId } })
  }

  const onOpenCreateEventDrawer = (tab: string = '1') => {
    setCreateEventInitialTab(tab)
    setCreateEventDrawerOpen(true)
  }

  const onCloseCreateEventDrawer = () => {
    setCreateEventDrawerOpen(false)
  }

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event
    const activeId = String(active.id)
    if (activeId.startsWith('job-') && active.data.current?.type === 'job') {
      setDraggedJob(active.data.current.job as SchedulingJob)
    }
    if (activeId.startsWith('route-') && active.data.current?.type === 'route') {
      setDraggedRoute(active.data.current.route as Route)
    }
    if (activeId.startsWith('reschedule-') && active.data.current?.type === 'reschedule-candidate') {
      setDraggedRescheduleCandidate(active.data.current.candidate as RescheduleCandidate)
    }
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    setDraggedJob(null)
    setDraggedRoute(null)
    setDraggedRescheduleCandidate(null)

    const activeId = String(active.id)
    if (activeId.startsWith('job-') && active.data.current?.type === 'job' && over) {
      const job = active.data.current.job as SchedulingJob
      const targetDate = over.data.current?.date as Dayjs | undefined
      const targetMinutes = over.data.current?.minutes as number | undefined

      if (targetDate) {
        setDroppedJobData({ job, date: targetDate, minutes: targetMinutes })
      }
    } else if (activeId.startsWith('route-') && active.data.current?.type === 'route' && over) {
      const route = active.data.current.route as Route
      const targetDate = over.data.current?.date as Dayjs | undefined

      if (targetDate) {
        setDroppedRouteData({ route, date: targetDate })
      }
    } else if (activeId.startsWith('reschedule-') && active.data.current?.type === 'reschedule-candidate' && over) {
      const candidate = active.data.current.candidate as RescheduleCandidate
      const targetDate = over.data.current?.date as Dayjs | undefined
      const targetMinutes = over.data.current?.minutes as number | undefined

      if (targetDate) {
        setDroppedRescheduleData({ candidate, date: targetDate, minutes: targetMinutes })
      }
    } else {
      if (scheduleState.view === ScheduleMode.Week) {
        weekViewRef.current?.handleDragEnd(event)
      } else if (scheduleState.view === ScheduleMode.Month) {
        monthViewRef.current?.handleDragEnd(event)
      }
    }
  }, [scheduleState.view])

  const handleJobModalClose = () => {
    setDroppedJobData(null)
  }

  const handleRouteModalClose = () => {
    setDroppedRouteData(null)
  }

  const handleRescheduleModalClose = () => {
    setDroppedRescheduleData(null)
  }

  return (
    <ScheduleProvider>
      <CalendarSearchHighlightProvider>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 165px)' }}>
          {scheduleState.reorderMode.enabled ? (
            <ReorderDayView
              selectedCrewLeadId={scheduleState.reorderMode.selectedCrewLeadId}
              date={scheduleState.selectedDate}
              onCancel={onExitReorderMode}
              onSave={onSaveReorder}
            />
          ) : (
            <>
              <ScheduleTitleBar
                state={scheduleState}
                onViewChange={onViewChange}
                onTodayClick={onTodayClick}
                onPrevClick={onPrevClick}
                onNextClick={onNextClick}
                onDateChange={onDateChange}
                onFiltersChange={onFiltersChange}
                onOpenCreateEventDrawer={onOpenCreateEventDrawer}
              />
              <div style={{ display: 'flex', flex: 1 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: 12 }}>
                  {scheduleState.view === ScheduleMode.Day && <ScheduleDayView state={scheduleState} onEnterReorderMode={onEnterReorderMode} />}
                  {scheduleState.view === ScheduleMode.Week && <ScheduleWeekView ref={weekViewRef} state={scheduleState} onDateChange={onDateChange} onOpenOptimize={onOpenOptimize} />}
                  {scheduleState.view === ScheduleMode.Month && <ScheduleMonthView ref={monthViewRef} state={scheduleState} onDateChange={onDateChange} onOpenOptimize={onOpenOptimize} />}
                </div>
                <CreateEventDrawer
                  open={createEventDrawerOpen}
                  onClose={onCloseCreateEventDrawer}
                  initialTab={createEventInitialTab}
                  selectedDate={scheduleState.selectedDate}
                />
              </div>
            </>
          )}
        </div>

        <DragOverlay dropAnimation={null}>
          {draggedJob ? <JobDragOverlay job={draggedJob} /> : null}
          {draggedRoute ? <RouteDragOverlay route={draggedRoute} /> : null}
          {draggedRescheduleCandidate ? <JobDragOverlay job={rescheduleCandidateToSchedulingJob(draggedRescheduleCandidate)} /> : null}
        </DragOverlay>

        {droppedJobData && (
          <CreateEventFromJobModal
            open={true}
            onClose={handleJobModalClose}
            job={droppedJobData.job}
            selectedDate={droppedJobData.date}
            selectedTime={droppedJobData.minutes}
          />
        )}

        {droppedRescheduleData && (
          <CreateEventFromJobModal
            open={true}
            onClose={handleRescheduleModalClose}
            job={rescheduleCandidateToSchedulingJob(droppedRescheduleData.candidate)}
            selectedDate={droppedRescheduleData.date}
            selectedTime={droppedRescheduleData.minutes}
            rescheduleFromEventId={droppedRescheduleData.candidate.calendarEventId}
            initialEvent={rescheduleCandidateToInitialEvent(droppedRescheduleData.candidate, droppedRescheduleData.date)}
          />
        )}

        {droppedRouteData && (
          <CreateEventFromRouteModal
            open={true}
            onClose={handleRouteModalClose}
            route={droppedRouteData.route}
            onSave={handleRouteModalClose}
            selectedDate={droppedRouteData.date}
          />
        )}

        {optimizeModalDate && (
          <OptimizeScheduleModal
            open={true}
            initialDate={optimizeModalDate}
            onCancel={onCancelOptimize}
            onSubmit={onSubmitOptimize}
          />
        )}
      </DndContext>
      </CalendarSearchHighlightProvider>
    </ScheduleProvider>
  )
})

export default ScheduleContent